/* global data, config */
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { SnackbarProvider, closeSnackbar } from "notistack";
import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  redirect,
  useNavigate,
} from "react-router-dom";

// Project imports
import adminRouteObj from "./components/AdminLayout";
import syncScheduleRouteObj from "./views/SyncSchedulePage";
import CostManagementPage from "./views/CostManagement";

import FullScreenLoader from "./components/FullScreenLoader";
import projectRootRouteObj from "./components/MainLayout";
import { LayoutProvider } from "./contexts/LayoutContext";
import {
  getLoggedInUserWithCustomClaims,
  getLoggedInUser,
} from "./helpers/auth";
import ThemeCustomization from "./themes";
import loginPageRouteObj from "./views/LoginPage";
import forgotPwPageRouteObj from "./views/ForgotPasswordPage";
import resetPwPageRouteObj from "./views/ResetPasswordPage";
import manangedSyncsRouteObj from "./views/ManagedSyncsIndexPage";
import matchingJobsRouteObj from "./views/MatchingJobsIndexPage";
import newMatchingJobsRouteObj from "./views/NewMatchingJobPage";
import listCutterJobsRouteObj from "./views/ListCutter/ListCutterJobsIndexPage";
import newListCutterJobRouteObj from "./views/ListCutter/NewListCutterJobPage";
import listCutterJobDetailsRouteObj from "./views/ListCutter/ListCutterJobDetails";
import CrosstabsStep1 from "./views/Crosstabs/steps/CrosstabsStep1";
import CrosstabsStep2 from "./views/Crosstabs/steps/CrosstabsStep2";
import CrosstabsStep3 from "./views/Crosstabs/steps/CrosstabsStep3";
import CrosstabsStep4 from "./views/Crosstabs/steps/CrosstabsStep4";
import CrosstabsStep5 from "./views/Crosstabs/steps/CrosstabsStep5";
import CrosstabsStep6 from "./views/Crosstabs/steps/CrosstabsStep6";
import CrosstabsReview from "./views/Crosstabs/steps/CrosstabsReview";
import crosstabsIndexRouteObj from "./views/Crosstabs/CrosstabsIndexPage";
import crosstabsDetailsRouteObj from "./views/Crosstabs/CrosstabsDetailsPage";

import welcomePageRouteObj from "./views/WelcomePage";
import buildRoute from "./components/buildRoute";
import ErrorBoundary from "./components/ErrorBoundary";

const router = createBrowserRouter([
  {
    path: "/",
    id: "root",
    Component: () => {
      const navigate = useNavigate();
      return <Outlet />;
    },
    children: [
      {
        index: true,
        loader: async () => redirect("/projects"),
      },
      {
        path: "login",
        ...loginPageRouteObj,
      },
      {
        path: "forgot-password",
        ...forgotPwPageRouteObj,
      },
      {
        path: "reset-password",
        ...resetPwPageRouteObj,
      },
      {
        path: "welcome",
        errorElement: <ErrorBoundary />,
        ...welcomePageRouteObj,
      },
      {
        path: "admin",
        id: "admin",
        ...adminRouteObj,
        children: [
          {
            path: "sync-schedule",
            ...syncScheduleRouteObj,
          },
          {
            path: "cost-management",
            element: <CostManagementPage />,
          },
        ],
      },
      {
        path: "projects",
        id: "projects",
        loader: async () => {
          const loggedInUser = await getLoggedInUserWithCustomClaims();
          return { loggedInUser };
        },
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            loader: async () => {
              const user = await getLoggedInUser();

              const allPartnersForUser = user?.partners;
              const defaultPartner = allPartnersForUser?.[0];
              const defaultProjectId = defaultPartner?.gcp_project_id;

              // If user belongs to no partners
              if (allPartnersForUser?.length === 0 || !defaultProjectId) {
                return redirect("/welcome");
              }

              return redirect(`/projects/${defaultProjectId}/syncs/managed`);
            },
          },
          {
            path: ":projectId",
            id: "projectRoot",
            ...projectRootRouteObj,
            children: [
              {
                path: "syncs",
                ...buildRoute({}),
                children: [
                  {
                    path: "managed",
                    ...manangedSyncsRouteObj,
                  },
                ],
              },
              {
                path: "matching-jobs",
                ...matchingJobsRouteObj,
              },
              {
                path: "matching-jobs/new",
                ...newMatchingJobsRouteObj,
              },
              {
                path: "list-cutter",
                children: [
                  {
                    index: true,
                    ...listCutterJobsRouteObj,
                  },
                  {
                    path: "new",
                    ...newListCutterJobRouteObj,
                  },
                  {
                    path: ":jobId",
                    ...listCutterJobDetailsRouteObj,
                  }
                ],
              },
              {
                path: "crosstabs",
                children: [
                  { index: true, ...crosstabsIndexRouteObj },
                  {
                    path: "new",
                    children: [
                      { path: "step1", element: <CrosstabsStep1 /> },
                      { path: "step2", element: <CrosstabsStep2 /> },
                      { path: "step3", element: <CrosstabsStep3 /> },
                      { path: "step4", element: <CrosstabsStep4 /> },
                      { path: "step5", element: <CrosstabsStep5 /> },
                      { path: "step6", element: <CrosstabsStep6 /> },
                      { path: "review", element: <CrosstabsReview /> },
                    ],
                  },
                  { path: ":crosstabId", ...crosstabsDetailsRouteObj }, 
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <ThemeCustomization>
      <LayoutProvider>
        <SnackbarProvider
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          action={(snackbarId) => (
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        >
          <RouterProvider
            router={router}
            fallbackElement={<FullScreenLoader />}
          />
        </SnackbarProvider>
      </LayoutProvider>
    </ThemeCustomization>
  );
}
